import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { FeatureGeoJson } from "../FeatureGeoJson";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { Feature, Map } from "ol";
import ImageWMSEx from "../net/ImageWMSEx";
import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import ImageLayer from "ol/layer/Image";
import { RoadkillLayerName } from "./RoadkillLayerName";
import { IAttributeConvert } from "@/attribute/convert/IAttributeConvert";
import { RoadkillCountermeasureStatusAttributeConvert } from "@/attribute/convert/RoadkillCountermeasureStatusAttributeConvert";
import { RoadkillOccurrencesStatusAttributeConvert } from "@/attribute/convert/RoadkillOccurrencesStatusAttributeConvert";
import { AttributeType } from "../define/AttributeType";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { getLayerKey } from "../LayerName";
import { LayerUtil } from "../util/LayerUtil";
import { Fill, RegularShape, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 12;

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * 選択状態のスタイルを返却
 */
const FUNCTION_getSelcetedStyle = (layerKey: string): Style => {
  if (layerKey.startsWith("t_roadkill_occurrences")) {
    return new Style({
      image: new CircleStyle({
        fill: new Fill({ color: "#ff69b4" }),
        radius: 7,
      }),
    });
  }
  if (layerKey.startsWith("t_roadkill_countermeasure")) {
    return new Style({
      image: new RegularShape({
        points: 3,
        radius: 5.5,
        stroke: new Stroke({ color: "#ff69b4", width: 3 }),
        displacement: [0, -1],
      }),
    });
  }
};

type AttributeConvertInfo = {
  contextMenu: string;
  attributeConvert: IAttributeConvert;
  selectedLayerStyle?: string;
};

/**
 * 属性変換クラス取得関数
 */
const FUNCTION_getRoadkillAttributeConvert = (
  key: string,
  feature: Feature
): AttributeConvertInfo => {
  switch (key) {
    /** ロードキル発生状況 */
    case "t_roadkill_occurrences_status":
      return {
        contextMenu: "ロードキル（発生状況）",
        attributeConvert: new RoadkillOccurrencesStatusAttributeConvert(
          feature
        ),
      };
    /** ロードキル対策実施状況 */
    case "t_roadkill_countermeasure_status":
      return {
        contextMenu: "ロードキル（対策実施状況）",
        attributeConvert: new RoadkillCountermeasureStatusAttributeConvert(
          feature
        ),
        selectedLayerStyle:
          feature.getGeometry().getType() == "Point"
            ? null
            : "t_roadkill_countermeasure_status_selected",
      };
  }
};

/**
 * GetMap用レイヤ物理名マッピング
 */
const GETMAP_LAYERNAME_MAP = [
  { physical: "t_roadkill_occurrences_status", logical: "ロードキル発生状況" },
  {
    physical: "t_roadkill_countermeasure_status",
    logical: "ロードキル対策実施状況",
  },
];
/**
 * ロードキルレイヤ出力クラス
 */
export class RoadkillLayerFactory implements ILayerFactory {
  /**
   * 表示WMSレイヤ
   */
  private _wmsLayer: ImageLayer<ImageWMSEx>;

  /**
   * 表示WMSソース
   */
  private _wmsImageSource: ImageWMSEx;

  /**
   * 表示対象ロードキルレイヤ
   */
  private _visibleLayers: RoadkillLayerName[] = [];

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return true;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * レイヤ定義を取得
   * @param map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): ImageLayer<ImageWMSEx>[] {
    if (this._wmsLayer) {
      return [this._wmsLayer];
    }
    //WMSレイヤの取得
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: "",
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._wmsLayer = new ImageLayer({
      source: this._wmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      minZoom: LAYER_MIN_ZOOMLEVEL,
    });
    return [this._wmsLayer];
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param layerNames レイヤ名リスト
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }

    // レイヤ論理名から、レイヤ論理名を抽出
    const cqlParameters = this._visibleLayers.map((layerDetail) => {
      const cqlParameterString =
        layerDetail.layerType === "ロードキル発生状況"
          ? `"発見_年" IN (${layerDetail.timeCode
              .map((ele) => "'" + ele + "'")
              .join(",")}) AND "動物種ID" IN (${layerDetail.id
              .map((ele) => "'" + ele + "'")
              .join(",")})`
          : `"対策分類ID" IN (${layerDetail.id
              .map((ele) => "'" + ele + "'")
              .join(",")})`;
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()),
        cqlParameterString
      );
    });
    const buffer = 5;
    const WMSGetter = new WMSGetFeatureManager();
    const selectedFeatureGeoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width,
      buffer
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = await Promise.all(
      selectedFeatures.map(async (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];

        const selectedId = `${layerKey}.${feature.get("ジオメトリID")}`;

        const wfsCqlParameterString = `"ジオメトリID"='${feature.get(
          "ジオメトリID"
        )}'`;
        const wfsCqlParameters = [
          new CqlParameter(layerKey, wfsCqlParameterString),
        ];
        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          wfsCqlParameters
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON().readFeatures(wfsGeoJson)[0];
        if (!wfsFeature) return;

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });
        selectFeature.setId(selectedId);
        const isPoint = wfsFeature.getGeometry().getType() == "Point";
        if (isPoint) {
          selectFeature.setStyle(FUNCTION_getSelcetedStyle(layerKey));
        }
        // featureGeoJSONを生成
        const geoJSON = new GeoJSON().writeFeature(wfsFeature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, selectedId);
        const attributeConvertInfo = FUNCTION_getRoadkillAttributeConvert(
          layerKey,
          wfsFeature
        );
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          AttributeType.Normal,
          attributeConvertInfo.attributeConvert as IAttributeConvert,
          !isPoint,
          layerKey,
          attributeConvertInfo.selectedLayerStyle,
          selectedId
        );
        return selectedFeatureInfo;
      })
    );
    return selectFeatureInfoList;
  }

  /**
   * 指定範囲の地物情報を取得
   * @param map 地図
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // レイヤ論理名から、レイヤ論理名を抽出
    // 表示対象TraVosLayerNameより時間コード・道路属性を取得し、cqlパラメータクラスを作成
    const cqlParameters = this._visibleLayers.map((layerDetail) => {
      const cqlParameterString =
        layerDetail.layerType === "ロードキル発生状況"
          ? `"発見_年" IN (${layerDetail.timeCode
              .map((ele) => "'" + ele + "'")
              .join(",")}) AND "動物種ID" IN (${layerDetail.id
              .map((ele) => "'" + ele + "'")
              .join(",")})`
          : `"対策分類ID" IN (${layerDetail.id
              .map((ele) => "'" + ele + "'")
              .join(",")})`;
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()),
        cqlParameterString
      );
    });
    const WFSGetter = new WFSGetFeatureManager();
    const selectedFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      cqlParameters,
      bBox
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = selectedFeatures.map(
      (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];
        // 属性変換クラス・attributeクラスを生成
        let attributeConvertInfo: AttributeConvertInfo = null;
        const attributeType: AttributeType = AttributeType.Normal;

        attributeConvertInfo = FUNCTION_getRoadkillAttributeConvert(
          layerKey,
          feature
        );

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();

        const selectFeature = new Feature({
          geometry,
        });

        const geoJSON = new GeoJSON().writeFeature(feature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          attributeType,
          attributeConvertInfo.attributeConvert as IAttributeConvert,
          false
        );
        return selectedFeatureInfo;
      }
    );
    return selectFeatureInfoList;
  }

  /**
   * 地物属性の有無を取得
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number
  ): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param layerNameList 対象ロードキルリスト
   */
  setVisible(isVisible: boolean, layerNameList: RoadkillLayerName[]) {
    this._visibleLayers = layerNameList;
    const layerKeys: string[] = [];
    const cqlParameters: string[] = [];
    if (isVisible && this._visibleLayers.length !== 0) {
      this._visibleLayers.forEach((layerDetail) => {
        const layerKey = GETMAP_LAYERNAME_MAP.find(
          (obj) => obj.logical === layerDetail.getLayerName()
        ).physical;

        const cqlParameter =
          layerKey === "t_roadkill_occurrences_status"
            ? `"発見_年" IN (${layerDetail.timeCode
                .map((ele) => "'" + ele + "'")
                .join(",")}) AND "動物種ID" IN (${layerDetail.id
                .map((ele) => "'" + ele + "'")
                .join(",")})`
            : `"対策分類ID" IN (${layerDetail.id
                .map((ele) => "'" + ele + "'")
                .join(",")})`;

        layerKeys.push(layerKey);
        cqlParameters.push(cqlParameter);
      });
    }
    if (cqlParameters.length == 0) {
      this._wmsLayer.setVisible(false);
    } else {
      this._wmsImageSource.updateParams({
        LAYERS: layerKeys.join(","),
        EXCEPTIONS: "application/json",
        CQL_FILTER: cqlParameters.join(";"),
      });
      this._wmsLayer.setVisible(isVisible);
    }
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    return this._wmsLayer?.getVisible() ? this._visibleLayers.length : 0;
  }

  /**
   * 表示レイヤが存在するか判定
   */
  isVisibleLayer(_map: Map) {
    return LayerUtil.isVisibleLayer(_map, this._wmsLayer);
  }
}
